import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard,
  Store,
  Users,
  Megaphone,
  Gift,
  Receipt,
  ShoppingCart,
  ChartNoAxesCombined,
  ShieldCheck,
  UserCog,
  Medal,
  Newspaper,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { formatAccountRole } from "../lib/display";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/lojas", icon: Store, label: "Lojas" },
  { to: "/admin/parceiros", icon: Users, label: "Parceiros" },
  { to: "/admin/campanhas", icon: Megaphone, label: "Campanhas" },
  { to: "/admin/publicacoes", icon: Newspaper, label: "Publicações" },
  { to: "/admin/regulamentos", icon: FileText, label: "Regulamentos" },
  { to: "/admin/recompensas", icon: Gift, label: "Recompensas" },
  { to: "/admin/resgates", icon: Receipt, label: "Resgates" },
  { to: "/admin/vendas", icon: ShoppingCart, label: "Vendas" },
  { to: "/admin/relatorios", icon: ChartNoAxesCombined, label: "Relatórios" },
  { to: "/admin/niveis", icon: Medal, label: "Níveis" },
  { to: "/admin/auditoria", icon: ShieldCheck, label: "Auditoria" },
  { to: "/admin/contas", icon: UserCog, label: "Contas", accountsOnly: true },
];

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="lg:hidden glass-nav sticky top-0 z-40 flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6 text-slate-700" />
        </button>
        <h1 className="font-heading font-bold text-xl text-primary">PIN Clube</h1>
        <div className="w-10" />
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-72 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
            <h1 className="font-heading font-bold text-2xl text-primary">
              PIN Clube
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navItems
              .filter((item) => {
                const isMaster = user?.tipo === "master" || user?.is_master;
                if (item.accountsOnly) return isMaster || user?.tipo === "admin" || user?.tipo === "loja";
                return true;
              })
              .map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    isActive
                      ? "bg-primary text-white shadow-lg shadow-blue-500/20"
                      : "bg-orange-50 text-orange-800 hover:bg-orange-100 hover:text-orange-900"
                  )
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
                <ChevronRight className="h-4 w-4 ml-auto opacity-50" />
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user?.nome?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{user?.nome}</p>
                <p className="text-sm text-slate-500">{formatAccountRole(user)}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full mt-3 justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-72 min-h-screen min-w-0">
        <div className="px-4 py-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
